import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type { OnUpdate } from '@/_schemas/types';
import { nameResolver, resetFormState } from '@/_shared';

import { invalidateCompanyQuery } from '../apis';
import { clientFormToUpdate, clientQueryToForm, fieldNames } from '../converters';
import type { CompanyFormSchema, CompanyQuerySchema, CompanyUpdateSchema } from '../schemas';

const DATES: string[] = [];

export function useRevisionHistory(
  form: UseFormReturn<CompanyFormSchema>,
  onUpdate: OnUpdate<CompanyUpdateSchema, { data: CompanyQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof CompanyFormSchema) => {
    return (
      field === 'phone_numbers' ||
      field === 'addresses' ||
      field === 'gnt_affiliations' ||
      field === 'non_gnt_affiliations' ||
      field === 'primary_contact' ||
      field === 'alternate_contacts' ||
      field === 'subsidiaries' ||
      field === 'primary_subsidiaries' ||
      field === 'domains' ||
      field === 'logo'
    );
  }, []);

  const onRevert: OnRevert<CompanyFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV: CompanyUpdateSchema = {} as CompanyUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = clientFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = clientFormToUpdate({ ...allFormValues });
        nV = { ...nV, [field]: oldValue };
      }

      const res = await onUpdate(nV, 'Company changes have been reverted.', true, false);
      if (res.data) {
        if (isPivot) {
          invalidateCompanyQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<CompanyQuerySchema, CompanyFormSchema> = useCallback((q) => {
    return clientQueryToForm({ ...q });
  }, []);

  const nameConverter: NameConverter<CompanyFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) return null;
    return fieldNames[f].name;
  }, []);

  const valueConverter: ValueConverter<CompanyFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];
    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    )
      return null;

    if (typeof v === 'object') {
      if ('label' in v) {
        return v.label;
      } else if ('name' in v) {
        return v.name;
      } else if ('first_name' in v && 'last_name' in v) {
        return nameResolver(v.first_name, v.last_name);
      }

      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v, DATES.includes(f));
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  const pivotClassifier: PivotClassifier<CompanyFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;
      if (isPivot) {
        switch (f) {
          case 'phone_numbers':
            changeSetExtractorType = 'PHONE_NUMBER_EMAIL';
            break;
          case 'addresses':
            changeSetExtractorType = 'ADDRESS';
            break;
          case 'alternate_contacts':
          case 'primary_contact':
            changeSetExtractorType = 'FIRST_NAME_LAST_NAME';
            break;
          case 'domains':
          case 'non_gnt_affiliations':
          case 'gnt_affiliations':
          case 'subsidiaries':
          case 'primary_subsidiaries':
            changeSetExtractorType = 'NAMED';
            break;
          case 'logo':
            changeSetExtractorType = 'MEDIA';
            break;
        }
      }
      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const pivotValueCalculator: PivotValueCalculator<CompanyFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      if (validatePivot(field)) {
        const nV = clientFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];

        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });
        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
